import { Clock3, LaptopMinimal } from "lucide-react";

import { Choice } from "../../../ui/Choice";
import type { Composer } from "../composer/use-composer";
import type { FooterOption } from "./options";

/** Live's option where a mode left offers none (`AUTO_OPTION_LABEL`): the model decides. */
const AUTO = { value: "", label: "auto" };

/** A footer menu's options in live's words: "auto" first where offered, then the values. */
function optionsOf<T>(option: FooterOption<T>) {
  return [
    ...(option.auto ? [AUTO] : []),
    ...option.values.map((each) => ({ value: String(each), label: String(each) })),
  ];
}

/**
 * The resolution, as live's: its laptop icon and tooltip "Resolution", no chevron on video, and
 * the resolutions of the modes left (issue 366), after live's "auto" where one of them offers
 * none (Kling v3 Pro, issue 345), which sends nothing.
 */
export function ResolutionChoice({ c }: { c: Composer }) {
  if (c.footer === null) return null;
  // A model that makes the video at its source's resolution (Veo's extensions, #210) decides
  // it: live's "auto" alone.
  const follows = c.followsSourceResolution;
  return (
    <Choice
      setting="Resolution"
      icon={<LaptopMinimal size={20} strokeWidth={1.5} />}
      chevron={false}
      tip="Resolution"
      value={follows ? "" : (c.footer.resolution ?? "")}
      options={follows ? [AUTO] : optionsOf(c.footer.options.resolution)}
      onChange={(next) => c.set({ resolution: next || null })}
    />
  );
}

/**
 * The length in seconds, from the modes left: live's `duration-select`, a bare number on the chip
 * and in the menu (`String(value)`), its clock and its tooltip "Video duration".
 */
export function DurationChoice({ c }: { c: Composer }) {
  // An edit's model with no lengths of its own reads live's "auto" (#209): the footer's options.
  if (c.footer === null) return null;
  return (
    <Choice
      setting="Duration (seconds)"
      icon={<Clock3 size={20} strokeWidth={1.5} />}
      chevron={false}
      tip="Video duration"
      value={c.footer.duration === null ? "" : String(c.footer.duration)}
      options={optionsOf(c.footer.options.duration)}
      onChange={(next) => c.set({ durationSeconds: next === "" ? null : Number(next) })}
      // Live's footer length greys out under Multi-Shot: the shots set it (capture 17b).
      disabledBecause={
        c.draft.multiShot && c.multiShot !== null
          ? `Duration is set by the shots, max ${c.multiShot.shot_seconds.max} sec`
          : undefined
      }
    />
  );
}
